import { memo, useMemo } from 'react';
import { Dropdownlist } from '@/components/Dropdownlist';

export interface AdvisoryFiltersState {
  status: string;
  client: number | null;
  npsdClientSiteId: number | null;
  advisoryTypeId: number | null;
}

interface AdvisoryFiltersProps {
  filters: AdvisoryFiltersState;
  filtersExpanded: boolean;
  activeFilterCount: number;
  clientItems: Array<{ label: string; value: number | null }>;
  siteItems: Array<{ label: string; value: number | null }>;
  typeItems: Array<{ label: string; value: number | null }>;
  onToggleFilters: () => void;
  onClear: () => void;
  onUpdateFilter: (field: keyof AdvisoryFiltersState, value: string | number | null) => void;
}

const STATUS_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Deleted', value: 'DELETED' },
];

export const AdvisoryFilters = memo(function AdvisoryFilters({
  filters,
  filtersExpanded,
  activeFilterCount,
  clientItems,
  siteItems,
  typeItems,
  onToggleFilters,
  onClear,
  onUpdateFilter,
}: AdvisoryFiltersProps) {
  const clientOptions = useMemo(
    () => [{ label: 'All clients', value: null }, ...clientItems],
    [clientItems]
  );
  const siteOptions = useMemo(
    () => [{ label: 'All sites', value: null }, ...siteItems],
    [siteItems]
  );
  const typeOptions = useMemo(
    () => [{ label: 'All types', value: null }, ...typeItems],
    [typeItems]
  );
  // Site is a real audience boundary only once a client is chosen (the picker
  // lists that client's sites); disabled and empty otherwise.
  const siteDisabled = filters.client == null;

  return (
    <>
      {/* Mobile Filters Toggle Bar */}
      <div className="mobile-filters-bar">
        <button className="btn-filters-toggle" onClick={onToggleFilters}>
          <i className="fa fa-filter"></i>
          Filters
          {activeFilterCount > 0 && (
            <span className="filter-count-badge">{activeFilterCount}</span>
          )}
          <i
            className={`fa ${filtersExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
          ></i>
        </button>
      </div>

      {/* Filters */}
      <div
        className={`filters-section${filtersExpanded ? ' filters-expanded' : ''}`}
      >
        <div className="filters-row">
          <div className="filter-group">
            <label>Status</label>
            <Dropdownlist
              value={filters.status}
              items={STATUS_OPTIONS}
              labelKey="label"
              valueKey="value"
              searchable={false}
              onChange={(value) => onUpdateFilter('status', value as string)}
            />
          </div>
          <div className="filter-group">
            <label>Client</label>
            <Dropdownlist
              value={filters.client}
              items={clientOptions}
              labelKey="label"
              valueKey="value"
              placeholder="All clients"
              searchable={clientOptions.length > 5}
              onChange={(value) => onUpdateFilter('client', value as number | null)}
            />
          </div>
          <div className="filter-group">
            <label>Site</label>
            <Dropdownlist
              value={filters.npsdClientSiteId}
              items={siteOptions}
              labelKey="label"
              valueKey="value"
              placeholder={siteDisabled ? 'Select a client first' : 'All sites'}
              searchable={siteOptions.length > 5}
              disabled={siteDisabled}
              onChange={(value) => onUpdateFilter('npsdClientSiteId', value as number | null)}
            />
          </div>
          <div className="filter-group">
            <label>Type</label>
            <Dropdownlist
              value={filters.advisoryTypeId}
              items={typeOptions}
              labelKey="label"
              valueKey="value"
              placeholder="All types"
              searchable={false}
              onChange={(value) => onUpdateFilter('advisoryTypeId', value as number | null)}
            />
          </div>
          <div className="filter-buttons">
            <button className="btn-clear" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </>
  );
});
